/**
 * CompletarEnforcer — validación post-LLM sin reintentos a la API.
 * Si la respuesta esperada no está anclada en el contexto, muta silenciosamente a desarrollo.
 */

import type { GeometryPayload, GeometryLabels } from '../types/mathGeometry';
import { repairCompletarGeometryNumericAnswers } from './geometryNumericCoherence';

type BlockLike = {
  mode?: string;
  payload?: Record<string, unknown>;
};

type ExamLike = {
  resourceType?: string;
  blocks: BlockLike[];
};

const DEFAULT_DESARROLLO_RUBRIC = [
  'Responde usando información del texto o contexto de la guía',
  'Justifica la respuesta con al menos un detalle del material',
];

export function stripDiacritics(text: string): string {
  return String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function normalizeAnchorText(text: string): string {
  return stripDiacritics(String(text || '').toLowerCase())
    .replace(/\s+/g, ' ')
    .trim();
}

function parseNumericToken(value: string): number | null {
  const cleaned = String(value || '')
    .replace(/[^\d,.-]/g, '')
    .replace(',', '.');
  if (!cleaned || cleaned === '.' || cleaned === '-') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function extractBracketAnswers(text: string): string[] {
  const answers: string[] = [];
  const re = /\[([^\]]+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const token = m[1]!.trim();
    if (token && !/^blank$/i.test(token)) answers.push(token);
  }
  return answers;
}

export function extractCompletarExpectedAnswers(payload: Record<string, unknown>): string[] {
  const fromText = extractBracketAnswers(String(payload.text || ''));
  const blanks = Array.isArray(payload.blanks)
    ? (payload.blanks as Array<{ answer?: string; accept?: string[] }>)
    : [];
  const fromBlanks = blanks.flatMap((b) => [b.answer, ...(b.accept ?? [])].filter(Boolean).map(String));
  return [...new Set([...fromBlanks, ...fromText].map((a) => a.trim()).filter(Boolean))];
}

function collectLabelText(labels?: GeometryLabels): string[] {
  if (!labels) return [];
  return Object.values(labels)
    .map((v) => String(v || '').trim())
    .filter(Boolean);
}

function collectGeometryNumbers(geometry?: GeometryPayload | null): number[] {
  if (!geometry) return [];
  const nums: number[] = [];
  const pushDim = (dims?: { width?: number; height?: number; radius?: number; base?: number }) => {
    if (!dims) return;
    for (const v of [dims.width, dims.height, dims.radius, dims.base]) {
      if (typeof v === 'number' && Number.isFinite(v)) nums.push(v);
    }
  };
  pushDim(geometry.dimensions);
  for (const part of geometry.parts ?? []) {
    pushDim(part.dimensions);
    nums.push(...collectLabelText(part.labels).map(parseNumericToken).filter((n): n is number => n != null));
  }
  nums.push(...collectLabelText(geometry.labels).map(parseNumericToken).filter((n): n is number => n != null));
  return [...new Set(nums)];
}

function collectGeometryText(geometry?: GeometryPayload | null): string {
  if (!geometry) return '';
  const chunks: string[] = [geometry.unit ?? ''];
  chunks.push(...collectLabelText(geometry.labels));
  for (const part of geometry.parts ?? []) {
    chunks.push(...collectLabelText(part.labels));
    const d = part.dimensions;
    if (d) {
      chunks.push(
        ...[d.width, d.height, d.radius, d.base]
          .filter((n): n is number => typeof n === 'number')
          .map(String),
      );
    }
  }
  const d = geometry.dimensions;
  if (d) {
    chunks.push(
      ...[d.width, d.height, d.radius, d.base]
        .filter((n): n is number => typeof n === 'number')
        .map(String),
    );
  }
  return chunks.filter(Boolean).join(' ');
}

function blockStimulusText(payload: Record<string, unknown>): string {
  const stimulus = payload.stimulus as { content?: string } | undefined;
  const fromStimulus = stimulus?.content ? String(stimulus.content) : '';
  const legacy = payload.sourceText ? String(payload.sourceText) : '';
  return [fromStimulus, legacy].filter(Boolean).join('\n');
}

function stripBlanks(text: string): string {
  return text.replace(/\[[^\]]*\]/g, ' ');
}

function isSelfContainedMathCompletar(payload: Record<string, unknown>): boolean {
  const text = String(payload.text || payload.title || payload.instruction || '');
  if (!text.trim()) return false;

  if (/\d+\s*\/\s*\d+/.test(text)) return true;
  if (/[><=]|compar|mayor|menor|equivalent|decimal|cent[eé]sim/i.test(text)) return true;
  if (/\[\s*\]/.test(text) && /\d/.test(stripBlanks(text))) return true;

  return false;
}

function isMathSubjectTag(subjectTag?: string): boolean {
  const tag = String(subjectTag || '').toLowerCase();
  return tag === 'matematica' || tag === 'math' || tag === 'matemática';
}

export function collectAnchorCorpus(
  priorBlocks: BlockLike[],
  currentPayload: Record<string, unknown>,
): string {
  const chunks: string[] = [];

  for (const block of priorBlocks) {
    const mode = String(block.mode || '').toLowerCase();
    const payload = block.payload && typeof block.payload === 'object' ? block.payload : {};
    if (mode === 'info_block') {
      chunks.push(String(payload.bodyText || payload.content || ''));
    }
    if (mode === 'fraction_model') {
      chunks.push(String(payload.instruction || ''));
      if (payload.numerator != null && payload.denominator != null) {
        chunks.push(`${payload.numerator}/${payload.denominator}`);
      }
    }
    if (mode === 'math_equation') {
      chunks.push(String(payload.instruction || ''));
      chunks.push(String(payload.equationLatex || ''));
    }
    if (mode === 'percent_grid') {
      chunks.push(String(payload.instruction || ''));
      if (payload.percentage != null) chunks.push(String(payload.percentage));
    }
    if (mode === 'geometry_canvas') {
      chunks.push(String(payload.bodyText || ''));
      chunks.push(String(payload.prompt || ''));
      chunks.push(String(payload.formulaHint || ''));
      chunks.push(collectGeometryText(payload.geometry as GeometryPayload | undefined));
    }
    if (mode === 'desarrollo' || STIMULUS_MODES.has(mode)) {
      chunks.push(blockStimulusText(payload));
      chunks.push(String(payload.context || ''));
      chunks.push(String(payload.referenceExpression || ''));
    }
    if (mode === 'completar') {
      chunks.push(String(payload.context || ''));
      chunks.push(String(payload.referenceExpression || ''));
      chunks.push(blockStimulusText(payload));
      chunks.push(collectGeometryText(payload.geometry as GeometryPayload | undefined));
    }
  }

  chunks.push(String(currentPayload.context || ''));
  chunks.push(String(currentPayload.referenceExpression || ''));
  chunks.push(blockStimulusText(currentPayload));
  chunks.push(collectGeometryText(currentPayload.geometry as GeometryPayload | undefined));

  return chunks.filter((c) => c.trim()).join('\n\n');
}

const STIMULUS_MODES = new Set(['arena', 'order', 'vincula', 'agrupa', 'point', 'completar']);

function significantWords(answer: string): string[] {
  return normalizeAnchorText(answer)
    .split(/[^a-z0-9áéíóúñ]+/i)
    .map((w) => stripDiacritics(w))
    .filter((w) => w.length >= 3);
}

function corpusIncludesNumber(corpus: string, value: number): boolean {
  const re = new RegExp(`\\b${String(value).replace('.', '[.,]')}\\b`);
  return re.test(corpus) || corpus.includes(String(value));
}

function geometrySupportsNumericAnswer(
  geometry: GeometryPayload | undefined,
  answer: string,
  completarText: string,
): boolean {
  if (!geometry) return false;
  const target = parseNumericToken(answer);
  if (target == null) return false;
  const dims = collectGeometryNumbers(geometry);
  if (dims.some((n) => Math.abs(n - target) < 0.01)) return true;

  const t = normalizeAnchorText(completarText);
  const w = geometry.dimensions?.width;
  const h = geometry.dimensions?.height;
  const r = geometry.dimensions?.radius;

  if (geometry.shapeType === 'rectangle' && w != null && h != null) {
    if (/area|superficie/.test(t) && Math.abs(target - w * h) < 0.5) return true;
    if (/perimetro/.test(t) && Math.abs(target - 2 * (w + h)) < 0.5) return true;
  }
  if (geometry.shapeType === 'circle' && r != null) {
    if (/perimetro|circunferencia/.test(t) && Math.abs(target - 2 * Math.PI * r) < 1) return true;
    if (/area|superficie/.test(t) && Math.abs(target - Math.PI * r * r) < 2) return true;
  }
  return false;
}

export function isAnswerAnchored(
  answer: string,
  corpus: string,
  opts: { geometry?: GeometryPayload; completarText?: string } = {},
): boolean {
  const raw = String(answer || '').trim();
  if (!raw || /^_+$/.test(raw)) return true;

  const normAnswer = normalizeAnchorText(raw);
  const normCorpus = normalizeAnchorText(corpus);
  if (!normCorpus) return false;

  if (normCorpus.includes(normAnswer)) return true;

  const words = significantWords(raw);
  if (words.length > 0 && words.every((w) => normCorpus.includes(w))) return true;

  const numeric = parseNumericToken(raw);
  if (numeric != null) {
    if (corpusIncludesNumber(normCorpus, numeric)) return true;
    if (geometrySupportsNumericAnswer(opts.geometry, raw, opts.completarText ?? '')) return true;
  }

  return false;
}

export function completarAnswersAreAnchored(
  payload: Record<string, unknown>,
  priorBlocks: BlockLike[],
): boolean {
  if (payload.rhythmPattern || payload.patternTokens) return true;
  if (payload.formulaLayout === 'fraction-percent') return true;

  const answers = extractCompletarExpectedAnswers(payload);
  if (!answers.length) return true;

  const corpus = collectAnchorCorpus(priorBlocks, payload);
  const geometry = payload.geometry as GeometryPayload | undefined;
  const completarText = String(payload.text || '');

  return answers.every((answer) => isAnswerAnchored(answer, corpus, { geometry, completarText }));
}

function stripBlankForPrompt(text: string): string {
  return text
    .replace(/\[[^\]]+\]/g, '')
    .replace(/_+/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();
}

function buildReflectivePrompt(completarText: string, answers: string[]): string {
  const stem = stripBlankForPrompt(completarText);
  const norm = normalizeAnchorText(stem);
  const answerHint = answers[0] ? stripDiacritics(answers[0].toLowerCase()) : '';

  if (/\d+\s*\/\s*\d+|[><=]|decimal|fracci|porcent|cent[eé]sim/.test(norm)) {
    if (/compar|mayor|menor|[><=]/.test(norm)) {
      return 'Compara las fracciones o valores del enunciado y justifica cuál es mayor, menor o si son equivalentes.';
    }
    if (/decimal|cent[eé]sim/.test(norm)) {
      return 'Convierte la fracción a decimal (o viceversa) y muestra el procedimiento en el espacio.';
    }
    return 'Resuelve la operación o completa el valor pedido usando los datos del enunciado.';
  }

  if (/sent[ií]a|sentimiento|emoci[oó]n|experimentaba/.test(norm)) {
    return 'Según el texto anterior, ¿qué sentimiento crees que experimentaba el personaje principal y por qué?';
  }
  if (/por qu[eé]|porque/.test(norm)) {
    return `Según el texto anterior, explica en tus propias palabras: ${stem.replace(/\.$/, '')}?`;
  }
  if (/[áa]rea|superficie|per[ií]metro/.test(norm)) {
    return 'Según el plano o datos de la actividad anterior, explica cómo calcularías la medida pedida y justifica tu respuesta.';
  }
  if (answerHint && /^\d/.test(answerHint)) {
    return 'Usa los datos del enunciado y explica cómo obtuviste el valor pedido.';
  }

  const base = stem.replace(/\.$/, '');
  if (base.length < 12) {
    return 'Responde en tus propias palabras usando la información del ejercicio.';
  }
  const question = base.endsWith('?') ? base : `${base}?`;
  return `${question.charAt(0).toUpperCase()}${question.slice(1)}`;
}

export function mutateCompletarToDesarrollo(block: BlockLike): BlockLike {
  const payload = block.payload && typeof block.payload === 'object' ? { ...block.payload } : {};
  const title = String(payload.title || payload.printTitle || 'Respuesta abierta').trim();
  const answers = extractCompletarExpectedAnswers(payload);
  const prompt = buildReflectivePrompt(String(payload.text || ''), answers);
  const stimulus = payload.stimulus;

  return {
    ...block,
    mode: 'desarrollo',
    payload: {
      title,
      prompt,
      rubric: [...DEFAULT_DESARROLLO_RUBRIC],
      minChars: 40,
      maxChars: 600,
      ...(stimulus ? { stimulus } : {}),
    },
  };
}

export function findLastGeometryBlock(priorBlocks: BlockLike[]): BlockLike | null {
  for (let i = priorBlocks.length - 1; i >= 0; i -= 1) {
    if (String(priorBlocks[i]?.mode || '').toLowerCase() === 'geometry_canvas') {
      return priorBlocks[i]!;
    }
  }
  return null;
}

export function attachGeometryToCompletar(block: BlockLike, geometryBlock: BlockLike | null): BlockLike {
  if (!geometryBlock?.payload || typeof geometryBlock.payload !== 'object') return block;
  const geo = geometryBlock.payload.geometry as GeometryPayload | undefined;
  if (!geo?.shapeType) return block;

  const payload = block.payload && typeof block.payload === 'object' ? { ...block.payload } : {};
  if (payload.geometry) return block;

  const bodyText = String(geometryBlock.payload.bodyText || '').trim();
  const next: Record<string, unknown> = {
    ...payload,
    geometry: geo,
  };
  if (!next.context && bodyText) {
    next.context = bodyText;
  }
  if (!next.referenceExpression) {
    const prompt = String(geometryBlock.payload.prompt || '').trim();
    if (prompt) next.referenceExpression = prompt;
  }

  return { ...block, payload: next };
}

export function enforceCompletarGuideBlocks<T extends ExamLike>(exam: T): T {
  if (exam.resourceType !== 'guide' && exam.resourceType !== 'exam') return exam;

  const subjectTag = String((exam as { subjectTag?: string }).subjectTag || '');
  const prior: BlockLike[] = [];
  let geometryConsumed = false;
  const blocks = exam.blocks.map((block) => {
    const mode = String(block.mode || '').toLowerCase();
    let current = block;

    if (mode === 'geometry_canvas') {
      geometryConsumed = false;
    }

    if (mode === 'completar') {
      if (!geometryConsumed) {
        const geometryBlock = findLastGeometryBlock(prior);
        if (geometryBlock) {
          current = attachGeometryToCompletar(current, geometryBlock);
          geometryConsumed = true;
        }
      }

      const payloadAfterAttach =
        current.payload && typeof current.payload === 'object' ? current.payload : {};
      if (payloadAfterAttach.geometry) {
        const repaired = repairCompletarGeometryNumericAnswers(payloadAfterAttach);
        if (repaired !== payloadAfterAttach) {
          current = { ...current, payload: repaired };
        }
      }

      const payload = current.payload && typeof current.payload === 'object' ? current.payload : {};
      const skipMutation =
        isSelfContainedMathCompletar(payload) ||
        (isMathSubjectTag(subjectTag) && /\[\s*\]/.test(String(payload.text || '')));

      if (!skipMutation && !completarAnswersAreAnchored(payload, prior)) {
        current = mutateCompletarToDesarrollo(current);
      }
    }

    prior.push(current);
    return current;
  });

  return { ...exam, blocks };
}
